import React, {useCallback, useMemo, useState} from "react";
import {Box, CircularProgress, DialogContent, DialogTitle, Divider, LinearProgress, Tooltip, Typography} from "@mui/material";
import {ReactComponent as SuccessSvg} from "../../../../../assets/images/art-like/quick-order-submitted.svg";
import Button from "../../../base/button/button";
import {routeFunctions} from "../../../../../core/models/static/routes/app-routes";
import Api from "../../../../../core/services/api/api";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {useEffectOnce} from "react-use";
import ImportingLottieAnimation from "../../../app-specific/lottie-animations/importing";
import {QuickOrderSummaryDataGridColumnNames} from "../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, DataGridDensities, useDataGridSavedState} from "../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../containers/data-grid";
import {ReactComponent as Passed} from "../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../assets/images/validations/failed.svg";
import ProductInformationPopover from "../../../app-specific/draggable-popover/product-information-popover";
import {Link} from "react-router-dom";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {QuickOrderSteps} from "../index";
import {formatMoney} from "../../../../../core/services/utils/utils";
import {usePartFormQualificationGate} from "../../../../features/part-form-qualification";


const dataGridSaveConfig = {
    name: 'QuickOrderSummarySectionDataGrid',
    version: 3,
}
const initialSubmissionProgress = {total: 0, progressed: 0, submitting: true, currentPartNo: '', tooltipMessage: '',}

const QuickOrderSummarySection = ({
                                      order,
                                      setOrder,
                                      createNewOrder,
                                      customer,
                                      items,
                                      changeDialogStatus,
                                      orderExtraInformation,
                                      getAllPricesForCustomer,
                                  }) => {
    const operatorAccessLevels = useAccessLevels();
    const {ensureAllQualified, GateDialogs} = usePartFormQualificationGate();
    const [submittedItems, setSubmittedItems] = useState([]);
    const [submissionProgress, setSubmissionProgress] = useState(initialSubmissionProgress);
    const [submissionChecklist, setSubmissionChecklist] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Prepares the items for submission by adjusting their prices based on the customer's pricing.
     * @type {function(*, *): *}
     */
    const prepareItems = useCallback((customerId, items) => {
        const customerPriceMap = getAllPricesForCustomer(customerId);
        return items.map(e => {
            const customerPrice = customerPriceMap[e.partNumber];
            return {
                ...e,
                price: customerPrice?.price === e.price && !!customerPrice ? null : e.price,
            }
        });
    }, [getAllPricesForCustomer]);

    /**
     * Creates a new order for the selected customer with the provided items.
     * @type {(function(*, *): Promise<undefined|T>)}
     */
    const createOrder = useCallback(async (customerId, items) => {
        const forApi = {
            customerId: customer?.id,
            parts: items.map(e => ({
                partNumber: e.partNumber,
                qty: e.qty,
                price: e.price,
                ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
                    warehouseCode: e.warehouseCode,
                }),
            }))
        }
        const response = await Api.createNewQuoteV2(forApi, true);
        if (!isMounted())
            return undefined;
        return response?.data;
    }, [customer?.id, isMounted, operatorAccessLevels.packages.sales.manageWarehousesInQuote])

    /**
     * Fetches the order information for the provided customer and order ids.
     * @type {(function(*, *): Promise<undefined|T>)}
     */
    const fetchOrderInformation = useCallback(async (customerId, orderId) => {
        const response = await Api.getQuoteInformationForCustomer(customerId, orderId, false);
        if (!isMounted())
            return undefined;
        return response?.data;
    }, [isMounted])

    /**
     * Updates the extra information for the provided order.
     * @type {(function(*, *, *, *): Promise<boolean|*>)|*}
     */
    const updateOrderExtraInformation = useCallback(async (customerId, orderId, orderInformation, orderExtraInformation) => {
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            customerPo: "customerPo" in orderExtraInformation ? orderExtraInformation.customerPo : orderInformation?.customerPo,
            fob: "fob" in orderExtraInformation ? orderExtraInformation.fob : orderInformation?.fob,
            requiredDate: "requiredDate" in orderExtraInformation ? orderExtraInformation.requiredDate : orderInformation?.requiredDate,
            referenceNo: "referenceNo" in orderExtraInformation ? orderExtraInformation.referenceNo : orderInformation?.referenceNo,
            territoryCode: "territory" in orderExtraInformation ? orderExtraInformation.territory : orderInformation?.territory,
            accountManagerCode: "accountManager" in orderExtraInformation ? orderExtraInformation.accountManager?.code : orderInformation?.accountManager?.code,
        };
        const response = await Api.updateQuotesPOAndFob(forApi, false);
        if (!isMounted())
            return false;
        return response?.resultFlag ?? false;
    }, [isMounted])

    /**
     * Updates the unit price of the item.
     * @type {function(*, *, *, *): Promise<*>}
     */
    const updateOrderItemsUnitPrice = useCallback(async (customerId, orderId, orderInformation, items) => {
        const orderItems = orderInformation?.items ?? [];
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            PartCountChangedList: items.filter(e => !!e.price).map((e, i) => ({
                ...(orderItems[i] ?? {}),
                partNo: e.partNumber,
                finalCount: e.qty,
                unitPrice: e.price,
                ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
                    whs: e.warehouseCode,
                }),
            }))
        };
        if (forApi.PartCountChangedList.length === 0)
            return true;
        const response = await Api.updateQuoteItems(forApi, undefined, false);
        if (!isMounted())
            return false;
        return response?.resultFlag ?? false;
    }, [isMounted, operatorAccessLevels.packages.sales.manageWarehousesInQuote])


    /**
     * Submits the quote for the selected customer and with the provided items:
     * - Calls the api for creating a new quote.
     * - If the api call was successful, calls the api for adding products to the added quote
     * - In case of success, sets the [order] in the inner state to the received value from the fist api call
     *   and calls the callback in the props for changing the dialog status
     *
     * @return {Promise<void>}
     */
    const submitQuote = useCallback(async () => {
        let preparedItems = prepareItems(customer?.id, items);
        const canProceed = await ensureAllQualified(
            customer?.id,
            preparedItems.map((e) => ({
                partNumber: e.partNumber,
                partLabel: e.partNumber,
            })),
        );
        if (!canProceed) {
            changeDialogStatus(QuickOrderSteps.ImportSection);
            return;
        }
        const needPriceUpdate = preparedItems.some(e => e.price !== null);
        const needOrderExtraInformationUpdate = orderExtraInformation && Object.keys(orderExtraInformation).length > 0;

        let priceUpdateSuccessful = true;
        const totalProgressItems =
            1 // create order
            + (needPriceUpdate || needOrderExtraInformationUpdate ? 1 : 0) // fetch order information if needed
            + (needOrderExtraInformationUpdate ? 1 : 0) // update order extra information if needed
            + (needPriceUpdate ? 1 : 0); // update prices if needed

        setSubmissionProgress(prevState => ({...prevState, total: totalProgressItems, progressed: 0, submitting: true, tooltipMessage: 'Creating Order',}));
        const order = await createOrder(customer?.id, preparedItems);
        if (!isMounted())
            return;

        if (!order) {
            changeDialogStatus(QuickOrderSteps.ImportSection);

        } else {

            setSubmissionChecklist(prevState => ([...prevState, {isSuccess: true, message: 'Order Created Successfully'}]));

            setSubmissionProgress(prevState => ({...prevState, progressed: prevState.progressed + 1, tooltipMessage: "Fetching Order Information"}));
            const orderInformation = await fetchOrderInformation(customer?.id, order.internalId);
            if (!isMounted())
                return;

            if (orderInformation) {

                if (needOrderExtraInformationUpdate) {
                    setSubmissionProgress(prevState => ({...prevState, progressed: prevState.progressed + 1, tooltipMessage: "Updating PO and Other Information"}));
                    const updateOrderExtraInfo = await updateOrderExtraInformation(customer?.id, order.internalId, orderInformation, orderExtraInformation ?? {});
                    if (!isMounted())
                        return;

                    if (updateOrderExtraInfo) {
                        setSubmissionChecklist(prevState => ([...prevState, {isSuccess: true, message: 'Order Extra Information Updated Successfully'}]));
                    } else {
                        setSubmissionChecklist(prevState => ([...prevState, {isSuccess: false, message: 'Failed to Update Order Extra Information'}]));
                    }

                }

                if (needPriceUpdate) {
                    setSubmissionProgress(prevState => ({...prevState, progressed: prevState.progressed + 1, tooltipMessage: "Updating Item Prices"}));
                    const updateOrderUnitPrices = await updateOrderItemsUnitPrice(customer?.id, order.internalId, orderInformation, preparedItems);
                    if (!isMounted())
                        return;

                    priceUpdateSuccessful = updateOrderUnitPrices;
                    if (updateOrderUnitPrices) {
                        setSubmissionChecklist(prevState => ([...prevState, {isSuccess: true, message: 'Order Item Prices Updated Successfully'}]));
                    } else {
                        // we need to reverse the pricing changes if the update failed
                        setSubmissionChecklist(prevState => ([...prevState, {isSuccess: false, message: 'Failed to Update Order Item Prices'}]));
                        preparedItems = preparedItems.map((e, i) => ({...e, price: null}));
                    }
                }
            }
        }

        setOrder(order);
        setSubmittedItems(prevState => {
            const toBeAdded = preparedItems.map(e => ({
                id: e.id,
                warehouseCode: e.warehouseCode,
                partNo: e.partNumber,
                qty: e.qty,
                price: e.price,
                resultFlag: true,
                message: 'Successfully Added '.concat(priceUpdateSuccessful && e.price != null ? '(using your desired-price)' : '(using customer\'s price-level)'),
            }));
            return [...prevState, ...toBeAdded];
        });
        setSubmissionProgress(prevState => ({...prevState, currentPartNo: '', tooltipMessage: '', total: preparedItems.length, progressed: preparedItems.length, submitting: false}));
    }, [prepareItems, customer?.id, items, ensureAllQualified, createOrder, isMounted, setOrder, changeDialogStatus, fetchOrderInformation, updateOrderExtraInformation, orderExtraInformation, updateOrderItemsUnitPrice])

    /**
     * As soon as the component mounts, submits the quote.
     */
    useEffectOnce(() => {
        void submitQuote();
    });

    /**
     * Memo version of percentage of data that dare processed
     * @type {number}
     */
    const progressPercentage = useMemo(() => {
        return Math.min(Math.round(submissionProgress.progressed / (submissionProgress.total === 0 ? 1 : submissionProgress.total) * 100), 100);
    }, [submissionProgress.progressed, submissionProgress.total])

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Status",
                name: QuickOrderSummaryDataGridColumnNames.status,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 50,
            },
            ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote ? [
                {
                    title: "Warehouse",
                    name: QuickOrderSummaryDataGridColumnNames.warehouse,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 80,
                },
            ] : []),
            {
                title: "Part Number",
                name: QuickOrderSummaryDataGridColumnNames.partNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Quantity",
                name: QuickOrderSummaryDataGridColumnNames.quantity,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.number,
                sortable: false,
                width: 80,
            },
            {
                title: "Price",
                name: QuickOrderSummaryDataGridColumnNames.price,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 100,
            },
            {
                title: "Message",
                name: QuickOrderSummaryDataGridColumnNames.errorDescription,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: {
                    min: 200,
                    size: 200,
                    type: DataGridColumnWidthTypes.flex,
                }
            },
        ], [operatorAccessLevels.packages.sales.manageWarehousesInQuote])

    const [dataGridColumns, , ,] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig?.name,
        version: dataGridSaveConfig?.version,
    });

    const dataGridRows = useMemo(() => submittedItems.map(e => ({
        key: e.id,
        data: e,
        cells: {
            [QuickOrderSummaryDataGridColumnNames.status]: submissionProgress.currentPartNo === e.partNo
                ? <CircularProgress color={'secondary'} size={20}/>
                : e.resultFlag ? <Passed/> : <Failed/>,
            [QuickOrderSummaryDataGridColumnNames.warehouse]: e.warehouseCode,
            [QuickOrderSummaryDataGridColumnNames.partNumber]:
                <ProductInformationPopover partNo={e.partNo}>
                    {e.partNo ?? ''}
                </ProductInformationPopover>,
            [QuickOrderSummaryDataGridColumnNames.quantity]: e.qty,
            [QuickOrderSummaryDataGridColumnNames.price]: e.price ? formatMoney(e.price) : "--",
            [QuickOrderSummaryDataGridColumnNames.errorDescription]: e.message,
        },
    })), [submittedItems, submissionProgress])

    return (
        <>
            {GateDialogs}
            <DialogTitle id={'scroll-dialog-title'}>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    Quick Order (Summary)
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent>
                <div className={'importing-in-process'}>
                    {
                        submissionProgress.submitting &&
                        <>
                            <ImportingLottieAnimation width={'80%'}/>
                            <Tooltip placement={'top'} arrow title={submissionProgress.tooltipMessage}>
                                <div className={'d-flex flex-column w-100 py-3 align-items-center'}>
                                    <LinearProgress
                                        className={'w-80'}
                                        variant={'buffer'}
                                        color={'secondary'}
                                        value={progressPercentage}
                                        valueBuffer={progressPercentage}
                                    />
                                    <p className={'identifier'}>{`${progressPercentage}%`}</p>
                                </div>
                            </Tooltip>
                            <p className={'warning-note'}>
                                We are submitting your order, please do not close this dialog.
                            </p>
                        </>
                    }
                    {
                        !submissionProgress.submitting &&
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'flex-start', flexDirection: 'column', minHeight: 180, gap: 2, marginBlock: 4,}}>
                            {
                                submissionChecklist.length === 0 &&
                                <SuccessSvg width={'300px'}/>
                            }
                            {
                                submissionChecklist.length > 0 &&
                                <Typography variant={'body1'} sx={{fontWeight: 500, color: 'text.primary', marginBlockBottom: 2}}>
                                    Submission Summary:
                                </Typography>
                            }
                            {
                                submissionChecklist.map((e, i) => (
                                    <Box key={i} sx={{display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start'}}>
                                        {e.isSuccess ? <Passed/> : <Failed/>}
                                        <Typography variant={'body2'} sx={{ml: 1, fontWeight: 500, color: e.isSuccess ? 'success.main' : 'error.main'}}>
                                            {e.message}
                                        </Typography>
                                    </Box>
                                ))
                            }
                        </Box>
                    }
                    <PartnerDataGrid
                        hideFooter
                        hideToolbar
                        {...(dataGridSaveConfig ?? {})}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        state={{
                            density: DataGridDensities.compact,
                        }}
                    />
                    {
                        !submissionProgress.submitting &&
                        <Button
                            className='mt-3'
                            size={'large'}
                            variant={'contained'}
                            color={'primary'}
                            component={Link}
                            to={routeFunctions.customer.sales.quotes.single(customer?.id ?? 0, order.internalId ?? 0)}
                            target={'_blank'}
                        >
                            Quote: {order?.orderNumber ?? ''}
                        </Button>
                    }
                    {
                        !submissionProgress.submitting &&
                        <Button
                            className='mt-3'
                            variant={'text'}
                            color={'primary'}
                            onClick={createNewOrder}
                        >
                            Create another quick order
                        </Button>
                    }
                </div>
            </DialogContent>
        </>
    );
}

export default QuickOrderSummarySection;
